import React from 'react';
import { Check, Mail, Phone, X } from 'lucide-react';
import { deriveDelegationValue } from '@/utils/investor/rosterUtils';
import type { Investor } from '@/types/investor';

type Channel = 'wholesale' | 'correspondent';

interface ChannelCardProps {
  channel: Channel;
  investor: Investor;
}

const formatApprovedDate = (dateString?: string): string => {
  if (!dateString) {
    return '';
  }
  const d = new Date(`${dateString}${dateString.length === 10 ? 'T00:00:00' : ''}`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Get channel-specific data with fallback to legacy fields for pre-migration records
const getChannelData = (investor: Investor, channel: Channel) => {
  if (channel === 'wholesale') {
    return {
      status: investor.status,
      approvedDate: investor.approvedAsOfDate,
      products: investor.wholesaleProducts || [],
      aeName: investor.wholesaleAeName,
      aePhone: investor.wholesaleAePhone,
      aeEmail: investor.wholesaleAeEmail,
      // Fall back to legacy epoRule/epdRule for pre-migration records
      epoRule: investor.wholesaleEpoRule || investor.epoRule,
      epdRule: investor.wholesaleEpdRule || investor.epdRule,
      canIntegrateOB: investor.wholesaleCanIntegrateOptimalBlue,
      hasIntegratedOB: investor.wholesaleHasIntegratedOptimalBlue,
      lpc: investor.wholesaleLenderPaidComp,
      additionalFee: investor.wholesaleAdditionalLendwiseFee
    };
  }

  return {
    status: investor.correspondentStatus,
    approvedDate: investor.correspondentApprovedAsOfDate,
    products: investor.correspondentProducts || [],
    aeName: investor.correspondentAeName,
    aePhone: investor.correspondentAePhone,
    aeEmail: investor.correspondentAeEmail,
    // Fall back to legacy epoRule/epdRule for pre-migration records
    epoRule: investor.correspondentEpoRule || investor.epoRule,
    epdRule: investor.correspondentEpdRule || investor.epdRule,
    canIntegrateOB: investor.correspondentCanIntegrateOptimalBlue,
    hasIntegratedOB: investor.correspondentHasIntegratedOptimalBlue,
    // Authoritative per-product delegation (item 6) with legacy delegatedType fallback.
    delegationLabel: deriveDelegationValue(investor)
  };
};

// Get Optimal Blue status
const getObStatus = (canIntegrate?: boolean, hasIntegrated?: boolean): 'integrated' | 'ready' | 'unavailable' => {
  if (hasIntegrated) {
return 'integrated';
}
  if (canIntegrate) {
return 'ready';
}
  return 'unavailable';
};

const contactInitials = (name?: string) => {
  if (!name?.trim()) {
return '?';
}
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  investor
}) => {
  const data = getChannelData(investor, channel);
  const channelLabel = channel === 'wholesale' ? 'Wholesale' : 'Correspondent';
  const obStatus = getObStatus(data.canIntegrateOB, data.hasIntegratedOB);
  const channelKey = channel === 'wholesale' ? 'w' : 'c';
  const isPricingActive = obStatus === 'integrated' || obStatus === 'ready';
  const aeInitials = contactInitials(data.aeName);
  const aeColor = channel === 'wholesale' ? 'var(--lw-w-color)' : 'var(--lw-c-color)';
  const epo = typeof data.epoRule === 'object' ? data.epoRule?.description : data.epoRule;
  const epd = typeof data.epdRule === 'object' ? data.epdRule?.description : data.epdRule;

  return (
    <div className={`lw-channel-card ${channelKey}`}>
      <div className="lw-channel-card-band">
        <span className="lw-channel-card-mark">{channel === 'wholesale' ? 'W' : 'C'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lw-channel-card-title">{channelLabel}</div>
          <div className="lw-channel-card-sub">
            {data.status === 'Approved' && data.approvedDate && formatApprovedDate(data.approvedDate)
              ? `Approved · ${formatApprovedDate(data.approvedDate)}`
              : data.status}
          </div>
        </div>
        <span className={`lw-status-pill ${data.status === 'Approved' ? 'ok' : data.status === 'Pending' ? 'warn' : 'neutral'}`}>
          <span className={`lw-dot ${data.status === 'Approved' ? 'ok' : data.status === 'Pending' ? 'warn' : 'neutral'}`} />
          {data.status}
        </span>
      </div>

      <div className="lw-channel-card-body">
        {data.products.length > 0 && (
          <div>
            <h3 className="lw-section-title">Products</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {data.products.map((product) => (
                <span key={product} className="lw-chip">{product}</span>
              ))}
            </div>
          </div>
        )}

        {channel === 'wholesale' ? (
          <div>
            {'lpc' in data && typeof data.lpc === 'number' && (
              <div className="lw-kv">
                <div className="lw-kv-key">LPC</div>
                <div className="lw-kv-value mono">{data.lpc.toFixed(2)}%</div>
              </div>
            )}
            {'additionalFee' in data && typeof data.additionalFee === 'number' && data.additionalFee > 0 && (
              <div className="lw-kv">
                <div className="lw-kv-key">Additional fee</div>
                <div className="lw-kv-value mono">{formatCurrency(data.additionalFee)}</div>
              </div>
            )}
            <div className="lw-kv">
              <div className="lw-kv-key">Optimal Blue</div>
              <div className="lw-kv-value">
                <span className="lw-ob-pill warn"><X size={12} /> Do not use OB — see Rate Sheet</span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="lw-kv">
              <div className="lw-kv-key">Delegation</div>
              <div className={`lw-kv-value ${data.delegationLabel ? '' : 'muted'}`}>
                {data.delegationLabel ?? 'Not set'}
              </div>
            </div>
            <div className="lw-kv">
              <div className="lw-kv-key">EPO</div>
              <div className={`lw-kv-value ${epo ? 'mono' : 'muted'}`}>{epo || 'N/A'}</div>
            </div>
            <div className="lw-kv">
              <div className="lw-kv-key">EPD</div>
              <div className={`lw-kv-value ${epd ? 'mono' : 'muted'}`}>{epd || 'N/A'}</div>
            </div>
            <div className="lw-kv">
              <div className="lw-kv-key">Optimal Blue</div>
              <div className="lw-kv-value">
                {isPricingActive ? (
                  <span className="lw-ob-pill ok"><Check size={12} /> Pricing Active</span>
                ) : (
                  <span className="lw-ob-pill bad"><Phone size={12} /> See Lock Desk</span>
                )}
              </div>
            </div>
          </div>
        )}

        {data.aeName && (
          <div>
            <h3 className="lw-section-title">Account Executive</h3>
            <div className="lw-contact" style={{ padding: 0 }}>
              <span className="lw-avatar lg" style={{ background: aeColor }}>{aeInitials}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="lw-contact-name">{data.aeName}</div>
                <div className="lw-contact-role">{channel === 'wholesale' ? 'Wholesale AE' : 'Correspondent AE'}</div>
                <div className="lw-contact-lines">
                  {data.aeEmail && <span className="lw-contact-line"><Mail size={11} />{data.aeEmail}</span>}
                  {data.aePhone && <span className="lw-contact-line"><Phone size={11} />{data.aePhone}</span>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelCard;
